import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import MenuIcon from '@material-ui/icons/Menu';

import materialTheme from '../../reactMaterialTheme';
import PublisherSelect from '../PublisherSelect/PublisherSelect';
import './EnhancedNavbar.scss';

const SubnavButton = props => {
  if (props.urlState === 'creativePreview.enhanced.index') {
    return (
      <a className="btn enhanced-ads-btn" href="/enhanced/new">
        {props.userLoggedIn ? 'Create Ad' : 'Get Started'}
      </a>
    );
  }
  return <span />;
};

SubnavButton.propTypes = {
  userLoggedIn: PropTypes.bool.isRequired,
  urlState: PropTypes.string.isRequired
};

const EnhancedNavbar = props => {
  const tabs = [
    {
      name: 'Examples',
      href: '',
      state: 'creativePreview.home',
      public: true
    },
    {
      name: 'All Ads',
      href: '/enhanced/ads',
      state: 'creativePreview.enhanced.index',
      public: false
    }
  ];

  const tabActiveClass = tab => {
    if (props.$state.current.name === tab.state) {
      return 'enhanced-navbar_subnav_tab--active';
    } else {
      return '';
    }
  };

  const showSubNav = () => {
    const blackList = ['creativePreview.enhanced.new'];
    return !blackList.includes(props.$state.current.name);
  };

  const showMobileMenu = () => {
    return window.outerWidth < 768;
  };

  const showPublisherSelect = () => {
    return (
      !showMobileMenu() &&
      ['creativePreview.enhanced.show', 'creativePreview.home'].includes(
        props.$state.current.name
      )
    );
  };

  return (
    props.$state.current.name !== 'creativePreview.enhanced.example' && (
      <MuiThemeProvider theme={materialTheme}>
        <div className="enhanced-navbar" data-testid="enhanced-navbar">
          <div className="enhanced-navbar_container">
            <a
              href={props.userLoggedIn ? '/enhanced/ads' : '/'}
              className="enhanced-navbar_logo-title-link"
            >
              <div className="enhanced-navbar_logo" />
              <div className="enhanced-navbar_title">Enhanced Ads</div>
            </a>
            {showMobileMenu() ? (
              <MobileEnhancedMenu
                tabs={tabs}
                userLoggedIn={props.userLoggedIn}
              />
            ) : (
              <div className="enhanced-ads_links">
                <a
                  className="enhanced-ads_link"
                  href="//sharethrough.com/enhancedads/"
                >
                  Enhanced Ads Program
                </a>
                {props.userLoggedIn ? (
                  <a
                    className="enhanced-ads_link"
                    data-method="delete"
                    href="/users/sign_out"
                  >
                    Logout
                  </a>
                ) : (
                  <a className="enhanced-ads_link" href="/users/sign_in">
                    Login
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        {showSubNav() && !showMobileMenu() && (
          <div className="enhanced-navbar_subnav">
            <div className="enhanced-navbar_container">
              <div className="enhanced-navbar_subnav_tabs-container">
                <div className="enhanced-navbar_subnav_tabs">
                  {tabs.map(tab => {
                    return (
                      (props.userLoggedIn || tab.public) && (
                        <a
                          className={`enhanced-navbar_subnav_tab h4 clickable ${tabActiveClass(
                            tab
                          )}`}
                          key={tab.href}
                          href={tab.href}
                        >
                          {tab.name}
                        </a>
                      )
                    );
                  })}
                </div>
              </div>
              {showPublisherSelect() && (
                <div className="mr5">
                  <PublisherSelect
                    CreativeService={props.CreativeService}
                    RenderService={props.RenderService}
                    showGroupSelect={false}
                  />
                </div>
              )}
              <SubnavButton
                userLoggedIn={props.userLoggedIn}
                urlState={props.$state.current.name}
              />
            </div>
          </div>
        )}
      </MuiThemeProvider>
    )
  );
};

const MobileEnhancedMenu = ({ userLoggedIn, tabs }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <div className="enhanced-navbar_menu-icon">
        <MenuIcon
          aria-haspopup="true"
          data-testid="enhanced-mobile-menu"
          onClick={() => setOpen(!open)}
        />
      </div>
      <Drawer
        anchor="top"
        open={open}
        onClose={() => setOpen(false)}
        classes={{
          paperAnchorTop: 'paper enhanced-navbar_drawer'
        }}
      >
        <List>
          {tabs.map(tab => {
            return (
              (userLoggedIn || tab.public) && (
                <ListItem key={tab.href} button component="a" href={tab.href}>
                  <ListItemText
                    classes={{ primary: 'enhanced-navbar_menu-text' }}
                  >
                    {tab.name}
                  </ListItemText>
                </ListItem>
              )
            );
          })}
          <Divider classes={{ root: 'enhanced-navbar_menu-divider' }} />
          <ListItem button component="a" href="//sharethrough.com/enhancedads/">
            <ListItemText classes={{ primary: 'enhanced-navbar_menu-text' }}>
              Enhanced Ads Program
            </ListItemText>
          </ListItem>
          {userLoggedIn ? (
            <ListItem
              button
              component="a"
              href="/users/sign_out"
              data-method="delete"
            >
              <ListItemText classes={{ primary: 'enhanced-navbar_menu-text' }}>
                Logout
              </ListItemText>
            </ListItem>
          ) : (
            <ListItem button component="a" href="/users/sign_in">
              <ListItemText classes={{ primary: 'enhanced-navbar_menu-text' }}>
                Login
              </ListItemText>
            </ListItem>
          )}
        </List>
      </Drawer>
    </Fragment>
  );
};

MobileEnhancedMenu.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      public: PropTypes.bool.isRequired
    })
  ).isRequired,
  userLoggedIn: PropTypes.bool.isRequired
};

EnhancedNavbar.propTypes = {
  userLoggedIn: PropTypes.bool,
  $state: PropTypes.object.isRequired,
  CreativeService: PropTypes.shape({
    resetCreative: PropTypes.func.isRequired
  }).isRequired,
  RenderService: PropTypes.shape({
    renderDemo: PropTypes.func.isRequired,
    setSite: PropTypes.func.isRequired
  }).isRequired
};

EnhancedNavbar.defaultProps = {
  userLoggedIn: false
};

export default EnhancedNavbar;
